import React, { useState, useRef, useEffect, type PropsWithChildren } from 'react';
import styles from './Dropdown.module.css';

type StateCallbackFunc = (state: boolean) => void;
type PositionType = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
interface DropdownProps extends PropsWithChildren {
  trigger: React.ReactNode;
  stateCallback?: StateCallbackFunc;
  position?: PositionType;
}

const Dropdown: React.FC<DropdownProps> = (props: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const setOpenState = (state: boolean) => {
    setIsOpen(state);
    if (props.stateCallback) {
        props.stateCallback(state);
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setOpenState(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getPositionStyles = (): React.CSSProperties => {
    if (!triggerRef.current) return {};

    const rect = triggerRef.current.getBoundingClientRect();
    const position: PositionType = props.position ?? 'bottom-right';
    switch (position) {
      case 'top-left':
        return {
          bottom: `${window.innerHeight - rect.top}px`,
          left: `${rect.left}px`,
        };
      case 'top-right':
        return {
          bottom: `${window.innerHeight - rect.top}px`,
          right: `${window.innerWidth - rect.right}px`,
        };
      case 'bottom-left':
        return {
          top: `${rect.bottom}px`,
          left: `${rect.left}px`,
        };
      case 'bottom-right':
        return {
          top: `${rect.bottom}px`,
          right: `${window.innerWidth - rect.right}px`,
        };
      default:
        return {};
    }
  };

  return (
    <div className={styles.dropdown}>
      <div ref={triggerRef} onClick={() => setOpenState(!isOpen)}>
        {props.trigger}
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            zIndex: 9999,
            ...getPositionStyles(),
          }}
        >
          {props.children}
        </div>
      )}
    </div>
  );
};

export default Dropdown;